import React, {
  ChangeEvent,
  CSSProperties,
  Dispatch,
  ReactElement,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";
import { convert, LocalDate, LocalDateTime } from "js-joda";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Grid from "@material-ui/core/Grid";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import IconButton from "@material-ui/core/IconButton";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import DeleteIcon from "@material-ui/icons/Delete";
import DateFnsUtils from "@date-io/date-fns";
import esLocale from "date-fns/locale/es";
import {
  KeyboardDatePicker,
  KeyboardTimePicker,
  MuiPickersUtilsProvider,
} from "@material-ui/pickers";
import { FormHelperText } from "@material-ui/core";
import axios from "axios";
import {List, Record} from "immutable";
import MuiPhoneNumber from "material-ui-phone-number";
import { parsePhoneNumberFromString } from "libphonenumber-js";

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: "1rem",
  },
  valid: {
    marginTop: "1rem",
    "& label": {
      color: "limegreen",
    },
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "limegreen",
        borderWidth: 1,
      },
      "&:hover fieldset": {
        borderColor: "limegreen",
        borderWidth: 1,
      },
    },
  },
}));

const tzOffset = (new Date()).getTimezoneOffset() * 60000;

function isBlank(s: string): boolean {
  const str = s.toString();
  return str.trim().length === 0;
}

export interface Field<V> {
  value: V;
  valid: boolean;
  validation: string;
  render(): ReactElement;
}

export type DateField = {
  value: LocalDate | null;
  valid: boolean;
  validation: string;
  render(): ReactElement;
};

export function useDateField(config?: {
  validate?(date: LocalDate): string;
  defaultValue?: LocalDate;
  disablePast?: boolean;
  disabled?: boolean;
  label?: string;
  name?: string;
  optional?: boolean;
}): DateField {
  const classes = useStyles();
  const defaultJsDate: Date | null = config?.defaultValue
    ? convert(config?.defaultValue).toDate()
    : null;
  const [jsDate, setJsDate] = useState<Date | null>(defaultJsDate);
  const [hasBeenBlurred, setHasBeenBlurred] = useState(Boolean(config?.defaultValue) ?? false);

  let date: LocalDate | null = null;
  if (jsDate) {
    try {
      const offsetDate = new Date(jsDate.getTime() - tzOffset);
      date = LocalDate.parse(offsetDate.toISOString().substring(0, 10));
    } catch (pokemonException) {}
  }

  function validate(): string {
    if (date == null) {
      return config?.optional ? "OK" : "Campo requerido";
    }

    const today = LocalDate.now();
    if (config?.disablePast && date.isBefore(today)) {
      return "La fecha no puede ser anterior a hoy";
    }

    return config?.validate?.(date) ?? "OK";
  }

  const validation = validate();
  const valid = validation === "OK";
  const error = hasBeenBlurred && !valid;

  function onBlur(): void {
    setHasBeenBlurred(true);
  }

  function render(): ReactElement {
    return (
      <MuiPickersUtilsProvider utils={DateFnsUtils} locale={esLocale}>
        <KeyboardDatePicker
          disablePast={config?.disablePast}
          disabled={config?.disabled}
          inputVariant="outlined"
          disableToolbar
          className={hasBeenBlurred && valid ? classes.valid : classes.root}
          format="dd/MM/yyyy"
          autoOk={true}
          label={config?.label}
          variant="inline"
          name={config?.name}
          value={jsDate}
          onChange={setJsDate}
          onBlur={onBlur}
          onClose={onBlur}
          helperText={error ? validation : ""}
          error={error}
          invalidDateMessage="Formato de fecha inválido"
          style={{ width: "100%" }}
        />
      </MuiPickersUtilsProvider>
    );
  }

  return { value: date, valid, validation, render };
}

export type TimeField = {
  value: LocalDateTime | null;
  valid: boolean;
  validation: string;
  render(): ReactElement;
};

export function useTimeField(config?: {
  validate?(date: LocalDateTime): string;
  defaultValue?: LocalDateTime;
  disablePast?: boolean;
  disabled?: boolean;
  label?: string;
  name?: string;
  optional?: boolean;
}): TimeField {
  const classes = useStyles();
  const defaultJsDate: Date | null = config?.defaultValue
    ? convert(config?.defaultValue).toDate()
    : null;
  const [jsDate, setJsDate] = useState<Date | null>(defaultJsDate);
  const [hasBeenBlurred, setHasBeenBlurred] = useState(Boolean(config?.defaultValue) ?? false);

  let date: LocalDateTime | null = null;
  if (jsDate) {
    try {
      const offsetDate = new Date(jsDate.getTime() - tzOffset);
      date = LocalDateTime.parse(offsetDate.toISOString().substring(0, 23));
    } catch (pokemonException) {}
  }

  function validate(): string {
    if (date == null) {
      return config?.optional ? "OK" : "Campo requerido";
    }

    return config?.validate?.(date) ?? "OK";
  }

  const validation = validate();
  const valid = validation === "OK";
  const error = hasBeenBlurred && !valid;

  function onBlur(): void {
    setHasBeenBlurred(true);
  }

  function render(): ReactElement {
    return (
      <MuiPickersUtilsProvider utils={DateFnsUtils} locale={esLocale}>
        <KeyboardTimePicker
          cancelLabel="Cancelar"
          disabled={config?.disabled}
          inputVariant="outlined"
          className={hasBeenBlurred && valid ? classes.valid : classes.root}
          label={config?.label}
          name={config?.name}
          value={jsDate}
          onChange={setJsDate}
          onBlur={onBlur}
          onClose={onBlur}
          helperText={error ? validation : ""}
          error={error}
          invalidDateMessage="Formato de hora inválido"
          keyboardIcon={<AccessTimeIcon />}
          style={{ width: "100%" }}
        />
      </MuiPickersUtilsProvider>
    );
  }

  return { value: date, valid, validation, render };
}

export type TextField = {
  value: string;
  valid: boolean;
  validation: string;
  validate(): string;
  render(props?: { style?: CSSProperties }): ReactElement;
  hasBeenBlurred: boolean;
  onChange(event: any): void;
  onBlur(): void;
  error: boolean;
  reset(): void;
};

export function useTextField(config?: {
  validate?(value: string): string;
  format?(value: string): string;
  optional?: boolean;
  type?: string;
  render?(field: TextField, props?: { style?: CSSProperties }): ReactElement;
  name?: string;
  label?: string;
  fullWidth?: boolean;
  defaultValue?: string;
  disabled?: boolean;
  onChange?: (event: any) => void;
  requiredFieldMessage?: string;
}): TextField {
  const classes = useStyles();
  const [value, setValue] = useState<string>(config?.defaultValue ?? "");
  const [hasBeenBlurred, setHasBeenBlurred] = useState(Boolean(config?.defaultValue) ?? false);

  function validate(): string {

      if((value.length>200) && (config?.label=="Título/Grado")){
          return "Tiene "+value.length+" caracteres, El máximo permitido es 200";
      }    
      if((value.length>100) && (config?.label=="Institución")){
          return "Tiene "+value.length+" caracteres, El máximo permitido es 100";
      }  

    return isBlank(value)
      ? config?.optional
        ? "OK"
        : (config?.requiredFieldMessage ?? "Campo requerido")
      : config?.validate?.(value) ?? "OK";
  }

  const validation = validate();
  const valid = validation === "OK";
  const error = hasBeenBlurred && !valid;

  function onChange(event: any): void {
    setValue(event.target.value);
    config?.onChange?.(event);
  }

  function onBlur(): void {
    if (config?.format && valid) {
      setValue(config?.format(value));
    }
    setHasBeenBlurred(true);
  }

  function reset(): void {
    setHasBeenBlurred(false);
    setValue(config?.defaultValue ?? "");
  }

  const field = {
    value,
    valid,
    validation,
    onChange,
    onBlur,
    validate,
    reset,
    hasBeenBlurred,
    error,
    render: function (props?: { style?: CSSProperties }): ReactElement {
      if (config?.render) {
        return config?.render(field, props);
      }

      return (
        <TextField
          variant="outlined"
          className={hasBeenBlurred && valid ? classes.valid : classes.root}
          fullWidth={config?.fullWidth}
          name={config?.name}
          label={config?.label}
          value={value}
          disabled={config?.disabled}
          onChange={onChange}
          onBlur={onBlur}
          helperText={error ? validation : ""}
          error={error}
        />
      );
    },
  };

  return field;
}

type EnumFieldConfig = {
  label?: string;
  optional?: boolean;
  options: { value: string; label: string }[];
  defaultValue?: string;
  disabled?: boolean;
  onChange?: (event: any) => void;
};

export function useRadioField(
  config: EnumFieldConfig & {
    name: string;
    defaultValue: string;
  }
): TextField {
  return useTextField({
    name: config.name,
    optional: config.optional,
    defaultValue: config.defaultValue,

    render(field: TextField, props?: { style: CSSProperties }): ReactElement {

      return (
          <FormControl className="mt-3 align-self-center" component="fieldset">
            <RadioGroup
              row
              name={config.name}
              value={field.value}
              onChange={field.onChange}
            >
              {config.options.map((option) => (
                  <FormControlLabel
                      key={option.value}
                      value={option.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      control={<Radio color="primary" />}
                      label={option.label}
                  />
              ))}
            </RadioGroup>
          </FormControl>
      )

      // return (
      //   <div style={props?.style}>
      //     {config.options.map((option) => (
      //       <React.Fragment key={option.value}>
      //         <input
      //           type="radio"
      //           id={`${config.name}-${option.value}`}
      //           name={config.name}
      //           value={option.value}
      //           onChange={field.onChange}
      //           onBlur={field.onBlur}
      //         />
      //         <label htmlFor={`${config.name}-${option.value}`}>
      //           {option.label}
      //         </label>
      //       </React.Fragment>
      //     ))}
      //   </div>
      // );
    },
  });
}

export function useSelectField(config: EnumFieldConfig): TextField {
  const classes = useStyles();
  const disabled = config.disabled;

  return useTextField({
    optional: config.optional,
    defaultValue: config.defaultValue,
    onChange: config.onChange,

    render(field: TextField, props?: { style: CSSProperties }): ReactElement {
      return (
        <FormControl
          variant="outlined"
          className={`mt-3 w-100 ${
            field.hasBeenBlurred && field.valid ? classes.valid : classes.root
          }`}
          error={field.error}
        >
          <InputLabel>{config.label}</InputLabel>
          <Select
            disabled={disabled}
            value={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
            onClose={field.onBlur}
            label={config.label}
          >
            {config.options.map((option) => (
              <MenuItem value={option.value} key={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>{field.error ? field.validation : ""}</FormHelperText>
        </FormControl>
      );
    },
  });
}

interface PhoneField extends Field<string> {}

export function usePhoneField(config?: {
  label?: string;
  defaultValue?: string,

}): PhoneField {
  const classes = useStyles();
  const [value, setValue] = useState(config?.defaultValue ?? "+56");
  const [hasBeenBlurred, setHasBeenBlurred] = useState(Boolean(config?.defaultValue) ?? false);



  function validate(): string {
    if (isBlank(value)) { 
      return "Campo requerido";
    }
    const phoneNumber = parsePhoneNumberFromString(value);
    if (!phoneNumber?.isValid()) {
      return "Teléfono inválido";
    }

    return "OK";
  }

  const validation = validate();
  const valid = validation === "OK";
  const error = !valid && hasBeenBlurred;

  function format(value: string): string {
    const phoneNumber = parsePhoneNumberFromString(value);
    return phoneNumber!.formatInternational();
  }

  function onChange(newValue: string): void {
    setValue(newValue);
  }

  function onBlur(): void {
    if (valid) {
      setValue(format);
    }
    setHasBeenBlurred(true);
  }


  return {
    value,
    validation,
    valid,

    render(): ReactElement {
      return (
          <MuiPhoneNumber
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              label={config?.label}
              helperText={error ? validation : ""}
              error={error}
              fullWidth
              defaultCountry="cl"
              preferredCountries={["cl"]}
              variant="outlined"
              className={hasBeenBlurred && valid ? classes.valid : classes.root}
          />
      );
    }
  };
}

export type Form = {
  isValid: boolean;
};

export function useForm(fields: Field<any>[]): Form {
  return { isValid: fields.every((field) => field.valid) };
}



export type Antecedente = {
  id?: number;
  nombre: string;
  estado: string;
  eliminable: boolean;
  isValid: boolean;
  deleted?: boolean;
};

type AntecedentesFieldProps = {
  index: number;
  antecedentes: Antecedente[];
  setAntecedentes: Dispatch<SetStateAction<Antecedente[]>>;
  onDelete: () => void;
};

export const AntecedenteField: React.FC<AntecedentesFieldProps> = (props) => {
  const { index, antecedentes, onDelete, setAntecedentes } = props;
  const antecedente = antecedentes[index];
  const defaultState = antecedente.estado;
  const [state, setState] = useState(defaultState);

  const toggleState = (newState) => {
    setState(newState);
    antecedentes[index].estado = newState;
    setAntecedentes([...antecedentes]);
  };

  const changeName = (event: any) => {
    antecedentes[index].nombre = event.target.value;
    setAntecedentes([...antecedentes]);
  };

  const filenameField = useTextField({
    label: "Nombre antecedente",
    defaultValue: antecedente.nombre,
    disabled: !antecedente.eliminable,
    fullWidth: true,
    onChange: changeName,
    name: index.toString(),
  });

  const isValid = filenameField.valid;
  useEffect(() => {
    antecedentes[index].isValid = isValid;
    setAntecedentes([...antecedentes]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, isValid, setAntecedentes]);

  if (antecedentes[index].deleted) {
    return null;
  }

  return (
    <Grid container spacing={0}>
      <Grid container spacing={2} item xs={11}>
        <Grid item xs={6}>
          {filenameField.render()}
        </Grid>
        <Grid item xs={6} className="d-flex justify-content-center">
          <FormControl className="mt-3 align-self-center" component="fieldset">
            <RadioGroup
              row
              name="estado"
              value={state}
              onChange={(event, newValue) => toggleState(newValue)}
            >
              <FormControlLabel
                value="obligatorio"
                disabled={!antecedente.eliminable}
                control={<Radio color="primary" />}
                label="Obligatorio"
              />
              <FormControlLabel
                value="opcional"
                disabled={!antecedente.eliminable}
                control={<Radio color="primary" />}
                label="Opcional"
              />
            </RadioGroup>
          </FormControl>
        </Grid>
      </Grid>
      <Grid container item xs={true} justify="flex-end">
        {antecedente.eliminable ? (
          <IconButton
            className="mt-3 align-self-center justify-self-end"
            style={{ height: "max-content" }}
            color="secondary"
            onClick={() => onDelete()}
          >
            <DeleteIcon />
          </IconButton>
        ) : null}
      </Grid>
    </Grid>
  );
};